import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash, FaDownload } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Styles from './scriptStyle.module.scss';
import axios from 'axios';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { LoaderCircle } from 'lucide-react';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { decodeBase64 } from '../../common/Utils/base64';
import api from '../../components/services/apiService';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { DataTable, ColumnConfig } from '../../components/Table';
import ReactMarkdown from 'react-markdown';
import { TwoLabelToggle } from '../../components/Toggle';
import { usePermissions } from '../../hooks/usePermissions';

const ScriptAutoTable: React.FC = () => {
  // local "typing" state and committed "search" state (applied on Enter)
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenantScript, setTenantScript] = useState<Script[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultParam = searchParams.get('default');
  const [isDefault, setIsDefault] = useState(defaultParam !== 'false');

  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [deleteScriptId, setDeleteScriptId] = useState<string | null>(null);
  const { selectedTenantId } = useTenantSelection();
  const navigate = useNavigate();
  const auth = useAuth();
  const { addToast } = useToast();
  const [expandedRows, setExpandedRows] = useState<{ [id: string]: boolean }>({});
  const { canEdit } = usePermissions();
  const canManageScripts = canEdit('scriptCenter');

  const CreateNewScript = () => navigate('/ScriptAutomation/CreateNewScript');

  const ITEMS_PER_PAGE = 20;
  const totalPages = paginationInfo?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const getScriptAutomation = async (page = 1, showInitial = false) => {
    if (showInitial) {
      setInitialLoading(true);
    } else {
      setTableLoading(true);
    }

    try {
      if (auth && auth.userData) {
        const tenantId = selectedTenantId;

        const queryParams = new URLSearchParams({
          isGlobalScript: isDefault ? 'true' : 'false',
          pageNumber: page.toString(),
          pageSize: ITEMS_PER_PAGE.toString(),
        });
        if (search.trim()) {
          queryParams.append('search', search.trim());
        }

        const response = await api.get(`${Project.api}tenantservice/TenantScript/${tenantId}/tenant-script?${queryParams}`);

        const { data } = response;
        setTenantScript(data.data ?? []);
        setPaginationInfo(data.pagination ?? null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setInitialLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    // initial fetch or when page/search/isDefault changes
    getScriptAutomation(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, search, isDefault]);

  const downloadScriptAutomation = async (dwnldId: string, isGlobalScript: boolean) => {
    try {
      if (auth && auth.userData) {
        let tenantId = selectedTenantId;
        const response = await api.get(`${Project.api}tenantservice/TenantScript/${tenantId}/download-tenant-script/${dwnldId}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;

        // Optional: get filename from header if available
        const disposition = response.headers['content-disposition'];
        let fileName = 'script-file.txt';
        if (disposition && disposition.includes('filename=')) {
          fileName = disposition.split('filename=')[1].replace(/['"]/g, '');
        }

        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const deleteScriptAutomation = async (scriptId: string) => {
    try {
      if (auth && auth.userData) {
        await api.delete(`${Project.api}tenantservice/TenantScript/${selectedTenantId}/delete-tenant-script/${scriptId}`);
        addToast('Script deleted successfully', 'success', 4000);
        // refresh current page after deletion
        getScriptAutomation(currentPage);
      }
    } catch (error) {
      console.error('Error deleting script:', error);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <LoaderCircle className="animate-spin w-10 h-10 text-white" />
      </div>
    );
  }

  // Add a state to track which row is expanded

  const columns: ColumnConfig[] = [
    {
      key: 'nature',
      header: 'Nature',
      render: (_: any, item: Script) => (item.isGlobalScript ? 'Default' : 'Custom'),
      width: '120px',
    },
    {
      key: 'scriptName',
      header: 'Script Name',
      width: '200px',
      render: (value: any, item: Script) => (
        <div
          className="line-clamp-2"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-all',
          }}
          title={item.scriptName}
        >
          {item.scriptName}
        </div>
      ),
    },
    {
      key: 'scriptDescription',
      header: 'Description',
      render: (value: any, item: Script) => {
        const desc = decodeBase64(item.scriptDescription);
        const isExpanded = expandedRows[item.tenantScriptStoreID];
        const isMarkdown = item.isGlobalScript;

        // Helper to toggle expand/collapse
        const toggleExpand = () => {
          setExpandedRows((prev) => ({
            ...prev,
            [item.tenantScriptStoreID]: !prev[item.tenantScriptStoreID],
          }));
        };

        // Clamp style for 3 lines
        const clampStyle = {
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
        };

        // Always show full if expanded, else clamp
        const containerStyle = isExpanded
          ? { fontSize: '14px', color: '#ccc', maxWidth: 600, wordBreak: 'break-word', whiteSpace: 'pre-line' }
          : { ...clampStyle, fontSize: '14px', color: '#ccc', maxWidth: 600, wordBreak: 'break-word', whiteSpace: 'pre-line' };

        // Detect if content is actually overflowing (for "Show more" button)
        // For simplicity, always show the button if not expanded
        // (You can refine this with a ref and scrollHeight check if needed)
        const showButton = !isExpanded;

        return (
          <div>
            <div style={containerStyle}>
              {isMarkdown ? (
                <ReactMarkdown
                  components={{
                    a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                    h1: ({ node, ...props }) => <span style={{ fontWeight: 600, color: '#fff' }} {...props} />,
                    h2: ({ node, ...props }) => <span style={{ fontWeight: 600, color: '#fff' }} {...props} />,
                    code: ({ node, ...props }) => <code style={{ background: '#222', borderRadius: 3, padding: '0 4px' }} {...props} />,
                  }}
                >
                  {desc}
                </ReactMarkdown>
              ) : (
                desc
              )}
            </div>
            <div>
              <button
                style={{
                  color: '#e53e3e',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  padding: 0,
                  marginTop: 2,
                  textDecoration: 'underline',
                }}
                onClick={toggleExpand}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            </div>
          </div>
        );
      },
    },
    {
      key: 'action',
      header: 'Action',
      render: (_: any, script: Script) => (
        <div className="flex gap-2">
          {!script.isGlobalScript && canManageScripts && (
            <>
              <button
                title="Edit Script"
                className="p-2 bg-gray-700 rounded hover:bg-gray-600"
                onClick={() => navigate(`/ScriptAutomation/EditScript/${script.tenantScriptStoreID}`)}
              >
                <FaEdit />
              </button>
              <button
                title="Delete Script"
                className="p-2 bg-gray-700 rounded hover:bg-gray-600"
                onClick={() => {
                  setDeleteScriptId(script.tenantScriptStoreID);
                  setShowDeleteModal(true);
                }}
              >
                <FaTrash />
              </button>
            </>
          )}
          <button
            title="Download Script"
            className="p-2 bg-gray-700 rounded hover:bg-gray-600"
            onClick={() => downloadScriptAutomation(script.tenantScriptStoreID, script.isGlobalScript)}
          >
            <FaDownload />
          </button>
        </div>
      ),
      width: '180px',
      align: 'left',
    },
  ];

  return (
    <div className="p-6 min-h-screen text-white">
      <div className="flex justify-between mb-4">
        <input
          type="text"
          placeholder="Search"
          className={Styles['searchInput']}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            // typing won't trigger API; only Enter will
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // commit search and reset to first page
              setSearch(searchInput);
              setCurrentPage(1);
            }
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TwoLabelToggle
            className="mr-4"
            checked={isDefault}
            leftLabel="Custom"
            rightLabel="Default"
            onChange={(newValue) => {
              setIsDefault(newValue);

              // Update query param
              const nextParams = new URLSearchParams(searchParams);
              nextParams.set('default', newValue ? 'true' : 'false');
              setSearchParams(nextParams);

              // Reset to first page
              setCurrentPage(1);
            }}
          />
          {canManageScripts && (
            <button className={Styles['createBtn']} onClick={() => CreateNewScript()}>
              Upload Custom Script
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto bg-1d1d1d text-white rounded-md shadow-md space-y-6">
        <table className="min-w-full table-auto text-sm">
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-6">
                  <div className="flex justify-center items-center">
                    <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  </div>
                </td>
              </tr>
            ) : (
              <DataTable columns={columns} data={tenantScript} loading={tableLoading} />
            )}
          </tbody>
        </table>
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-[#1c1c1c] p-6 rounded shadow-md text-white">
            <p>Are you sure you want to delete this script?</p>
            <div className="flex justify-end gap-4 mt-4">
              <button className="px-4 py-2 bg-gray-700 rounded hover:bg-gray-600" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 rounded hover:bg-red-700"
                onClick={() => {
                  if (deleteScriptId) deleteScriptAutomation(deleteScriptId);
                  setShowDeleteModal(false);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} loading={tableLoading} onPageChange={handlePageChange} />
    </div>
  );
};

export default ScriptAutoTable;
